import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Layers, ChevronDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";

const LEVELS = [["pawn", "Peão"], ["knight", "Cavalo"], ["bishop", "Bispo"], ["rook", "Torre"], ["queen", "Dama"], ["king", "Rei"]];
const inp = "rounded-lg bg-chessup-deep border border-chessup-border px-3 py-2 text-sm text-chessup-text focus:border-chessup-turq focus:outline-none w-full";

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [modules, setModules] = useState({});

  const load = () => api.get("/admin/courses").then(({ data }) => setCourses(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    const { data } = await api.post("/admin/courses", { title: "Novo curso", level_key: "pawn", published: false });
    toast.success("Curso criado");
    load();
  };
  const save = async (c) => { await api.put(`/admin/courses/${c.id}`, c); toast.success("Curso salvo"); load(); };
  const del = async (id) => { if (!window.confirm("Excluir curso?")) return; await api.delete(`/admin/courses/${id}`); toast.success("Curso excluído"); load(); };

  const loadModules = async (courseId) => {
    const { data } = await api.get("/admin/modules", { params: { course_id: courseId } });
    setModules((m) => ({ ...m, [courseId]: data }));
  };
  const toggle = (id) => { if (expanded === id) setExpanded(null); else { setExpanded(id); loadModules(id); } };
  const addModule = async (courseId) => { await api.post("/admin/modules", { course_id: courseId, title: "Novo módulo", order: (modules[courseId]?.length || 0) + 1 }); loadModules(courseId); };
  const saveModule = async (m) => { await api.put(`/admin/modules/${m.id}`, m); toast.success("Módulo salvo"); };
  const delModule = async (m) => { if (!window.confirm("Excluir módulo?")) return; await api.delete(`/admin/modules/${m.id}`); loadModules(m.course_id); };

  const upd = (id, patch) => setCourses((cs) => cs.map((c) => c.id === id ? { ...c, ...patch } : c));
  const updModule = (courseId, id, patch) => setModules((m) => ({ ...m, [courseId]: m[courseId].map((x) => x.id === id ? { ...x, ...patch } : x) }));

  return (
    <div className="p-5 sm:p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl text-chessup-text">Cursos</h1>
        <button onClick={create} data-testid="admin-create-course" className="inline-flex items-center gap-1.5 rounded-full bg-chessup-gold px-4 py-2 text-sm font-semibold text-chessup-deep hover:bg-chessup-goldlight"><Plus className="h-4 w-4" /> Novo curso</button>
      </div>
      <div className="space-y-4">
        {courses.map((c) => (
          <div key={c.id} className="rounded-xl border border-chessup-border bg-chessup-card p-5" data-testid={`admin-course-${c.id}`}>
            <div className="grid sm:grid-cols-2 gap-3">
              <input className={inp} value={c.title} onChange={(e) => upd(c.id, { title: e.target.value })} placeholder="Título" />
              <select className={inp} value={c.level_key} onChange={(e) => upd(c.id, { level_key: e.target.value })}>{LEVELS.map(([k, n]) => <option key={k} value={k}>{n}</option>)}</select>
              <input className={inp} value={c.difficulty || ""} onChange={(e) => upd(c.id, { difficulty: e.target.value })} placeholder="Dificuldade" />
              <input className={inp} value={c.cover || ""} onChange={(e) => upd(c.id, { cover: e.target.value })} placeholder="URL da capa" />
              <textarea className={`${inp} sm:col-span-2`} value={c.description || ""} onChange={(e) => upd(c.id, { description: e.target.value })} placeholder="Descrição" rows={2} />
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-chessup-subtext">
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!c.published} onChange={(e) => upd(c.id, { published: e.target.checked })} /> Publicado</label>
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!c.is_new} onChange={(e) => upd(c.id, { is_new: e.target.checked })} /> Novo</label>
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!c.featured} onChange={(e) => upd(c.id, { featured: e.target.checked })} /> Destaque</label>
              <div className="flex-1" />
              <button onClick={() => save(c)} className="inline-flex items-center gap-1.5 rounded-lg bg-chessup-turq/15 text-chessup-turq px-3 py-1.5"><Save className="h-4 w-4" /> Salvar</button>
              <button onClick={() => del(c.id)} className="inline-flex items-center gap-1.5 rounded-lg bg-destructive/15 text-red-200 px-3 py-1.5"><Trash2 className="h-4 w-4" /></button>
            </div>
            <button onClick={() => toggle(c.id)} className="mt-3 inline-flex items-center gap-1.5 text-sm text-chessup-subtext hover:text-chessup-turq">
              {expanded === c.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} <Layers className="h-4 w-4" /> Módulos
            </button>
            {expanded === c.id && (
              <div className="mt-3 pl-4 border-l border-chessup-border space-y-2">
                {(modules[c.id] || []).map((m) => (
                  <div key={m.id} className="flex items-center gap-2">
                    <input className={inp} value={m.title} onChange={(e) => updModule(c.id, m.id, { title: e.target.value })} />
                    <input type="number" className="w-16 rounded-lg bg-chessup-deep border border-chessup-border px-2 py-2 text-sm text-chessup-text" value={m.order} onChange={(e) => updModule(c.id, m.id, { order: parseInt(e.target.value) || 0 })} />
                    <button onClick={() => saveModule(m)} className="rounded-lg bg-chessup-turq/15 text-chessup-turq px-2 py-2"><Save className="h-4 w-4" /></button>
                    <button onClick={() => delModule(m)} className="rounded-lg bg-destructive/15 text-red-200 px-2 py-2"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <button onClick={() => addModule(c.id)} className="inline-flex items-center gap-1.5 text-sm text-chessup-turq"><Plus className="h-4 w-4" /> Adicionar módulo</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
